import React from "react";
import { FilterRoomsInputs } from "../../types";
import { ActiveSearchFilter, SearchDropdownForm, SearchItemConfig } from "src/components/Form/search-dropdown-form";

type Props = {
  updateFilterQuery: (filter: FilterRoomsInputs) => void;
  initialQuery?: FilterRoomsInputs;
};

type SearchKey = "id" | "userId" | "status" | "category" | "privacy" | "langCode";

const searchItems: Record<SearchKey, SearchItemConfig> = {
  id: {
    type: "input",
    placeholder: "Enter a room ID",
  },
  userId: {
    type: "input",
    placeholder: "Enter host user ID",
  },
  status: {
    type: "select",
    placeholder: "All Status",
    options: [
      { value: "INITIATED", text: "Initiated" },
      { value: "SCHEDULED", text: "Scheduled" },
      { value: "ACTIVE", text: "Active" },
      { value: "ENDED", text: "Ended" },
      { value: "CANCELLED", text: "Cancelled" },
      { value: "EXPIRED", text: "Expired" },
    ],
  },
  category: {
    type: "select",
    placeholder: "All Categories",
    options: [
      { value: "CLASSROOM", text: "Classroom" },
      { value: "COMMUNITY", text: "Community" },
    ],
  },
  privacy: {
    type: "select",
    placeholder: "All Privacy",
    options: [
      { value: "PUBLIC", text: "Public" },
      { value: "FOLLOWINGS", text: "Followings" },
      { value: "PRIVATE", text: "Private" },
    ],
  },
  langCode: {
    type: "input",
    placeholder: "Enter a language code, e.g., en or nl",
  },
};

const labelMap: Record<keyof typeof searchItems, string> = {
  id: "Room ID",
  userId: "Host User",
  status: "Status",
  category: "Category",
  privacy: "Privacy",
  langCode: "Language",
};

export const RoomsSearch: React.FC<Props> = ({ updateFilterQuery, initialQuery = {} }) => {
  const handleSubmit = (key: SearchKey, value: string) => {
    const nextFilters: FilterRoomsInputs = { ...initialQuery };

    if (!value) {
      switch (key) {
        case "id":
          delete nextFilters.id;
          break;
        case "userId":
          delete nextFilters.userId;
          break;
        case "status":
          delete nextFilters.status;
          break;
        case "category":
          delete nextFilters.category;
          break;
        case "privacy":
          delete nextFilters.privacy;
          break;
        case "langCode":
          delete nextFilters.langCode;
          break;
      }

      updateFilterQuery(nextFilters);
      return;
    }

    switch (key) {
      case "status":
        nextFilters.status = value as FilterRoomsInputs["status"];
        break;
      case "category":
        nextFilters.category = value as FilterRoomsInputs["category"];
        break;
      case "privacy":
        nextFilters.privacy = value as FilterRoomsInputs["privacy"];
        break;
      case "id":
        nextFilters.id = value;
        break;
      case "userId":
        nextFilters.userId = value;
        break;
      case "langCode":
        nextFilters.langCode = value;
        break;
    }

    updateFilterQuery(nextFilters);
  };

  const activeFilters = Object.entries(initialQuery).reduce<ActiveSearchFilter<SearchKey>[]>((filters, [key, value]) => {
    if (!value || !(key in searchItems)) return filters;

    const searchKey = key as keyof typeof searchItems;
    const config = searchItems[searchKey];
    const rawValue = String(value);
    const resolvedValue =
      config.type === "select" ? config.options?.find((option) => option.value === rawValue)?.text || rawValue : rawValue;

    filters.push({
      key: searchKey,
      label: labelMap[searchKey],
      value: resolvedValue,
    });

    return filters;
  }, []);

  return (
    <div className="pt-5 pb-7">
      <SearchDropdownForm
        searchItems={searchItems}
        handleSubmit={handleSubmit}
        activeFilters={activeFilters}
        onRemoveFilter={(key) => handleSubmit(key, "")}
        onClearAll={() => updateFilterQuery({})}
      />
    </div>
  );
};
